import { motion } from "framer-motion";
import { Droplets, Flame, Sprout, Waves, Biohazard, Hammer } from "lucide-react";

const REBUILD_IMG =
  "https://images.unsplash.com/photo-1444419988131-046ed4e5ffd6?crop=entropy&cs=srgb&fm=jpg&q=85";

const cardReveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.8, ease: [0.215, 0.61, 0.355, 1] },
  }),
};

const SERVICES = [
  {
    id: "water",
    icon: Droplets,
    accent: "#38BDF8",
    title: "Water Damage Restoration",
    tagline: "Rapid Extraction & Structural Drying",
    desc: "Thermal moisture detection, industrial dehumidification, and complete structural drying for flooded basements, burst pipes, and storm damage.",
    span: "md:col-span-4",
  },
  {
    id: "fire",
    icon: Flame,
    accent: "#FB923C",
    title: "Fire & Smoke Restoration",
    tagline: "Soot Removal & Odor Neutralization",
    desc: "Smoke odor neutralization, soot removal, charred structure treatment, and deep cleaning of your damaged belongings.",
    span: "md:col-span-4",
  },
  {
    id: "mold",
    icon: Sprout,
    accent: "#4ADE80",
    title: "Mold Remediation",
    tagline: "Containment & Air Quality Restoration",
    desc: "HEPA air scrubbing, negative-pressure containment, moisture source elimination, and certified air quality testing.",
    span: "md:col-span-4",
  },
  {
    id: "sewage",
    icon: Waves,
    accent: "#9CA3AF",
    title: "Sewage Cleanup",
    tagline: "Bio-Sanitization & Extraction",
    desc: "Category 3 black water extraction, deep disinfection, odor counteractants, and safe removal of compromised materials.",
    span: "md:col-span-6",
  },
  {
    id: "biohazard",
    icon: Biohazard,
    accent: "#F87171",
    title: "Biohazard & Trauma Cleanup",
    tagline: "Discreet, Compassionate & Certified",
    desc: "OSHA-compliant decontamination, trauma site mitigation, and pathogen neutralization — handled with absolute dignity and privacy for your family.",
    span: "md:col-span-6",
  },
];

function ServiceCard({ service, index }) {
  const Icon = service.icon;
  return (
    <motion.article
      custom={index}
      variants={cardReveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`service-card-${service.id}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#111116] p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-300/25 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] sm:p-8 ${service.span}`}
    >
      <div
        className="absolute left-0 top-0 h-[2px] w-0 transition-all duration-500 group-hover:w-full"
        style={{ background: service.accent }}
      />
      <div
        className="mb-6 inline-flex rounded-xl border border-white/10 p-3"
        style={{ color: service.accent, background: `${service.accent}14` }}
      >
        <Icon className="h-6 w-6" strokeWidth={1.5} />
      </div>
      <h3 className="font-serif-display text-xl font-medium text-white sm:text-2xl">{service.title}</h3>
      <p className="font-mono-data mt-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: service.accent }}>
        {service.tagline}
      </p>
      <p className="mt-4 text-sm leading-relaxed text-slate-400">{service.desc}</p>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
          className="mb-16 max-w-2xl"
        >
          <p className="font-mono-data mb-4 text-xs uppercase tracking-[0.35em] text-purple-400">What We Restore</p>
          <h2 className="font-serif-display text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            One call. <span className="italic text-purple-400">Every disaster</span> covered.
          </h2>
          <p className="mt-5 text-base font-light leading-relaxed text-slate-400">
            From the first emergency extraction to the final coat of paint, Redemption Restoration
            handles the entire recovery — so you never juggle contractors during a crisis.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          <motion.article
            custom={0}
            variants={cardReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            data-testid="service-card-rebuild"
            className="group relative overflow-hidden rounded-2xl border border-amber-400/20 md:col-span-8"
          >
            <img
              src={REBUILD_IMG}
              alt="Home rebuild and structural restoration in progress"
              className="absolute inset-0 h-full w-full object-cover opacity-35 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-[#08080A]/70 to-[#08080A]/30" />
            <div className="relative flex h-full min-h-[320px] flex-col justify-end p-7 sm:p-10">
              <div className="mb-5 inline-flex w-fit rounded-xl border border-amber-400/25 bg-amber-400/10 p-3 text-amber-400">
                <Hammer className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif-display text-2xl font-medium text-white sm:text-3xl">
                Full Home Rebuild
              </h3>
              <p className="font-mono-data mt-2 text-[10px] uppercase tracking-[0.22em] text-amber-400">
                Putting Your House Back Together
              </p>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300">
                Framing, drywall, roofing, electrical, and finish carpentry — a turnkey rebuild that
                returns your home to better-than-before condition. Most restoration companies stop at
                cleanup. We stay until you are home again.
              </p>
            </div>
          </motion.article>

          {SERVICES.slice(0, 1).map((s, i) => (
            <ServiceCard key={s.id} service={{ ...s, span: "md:col-span-4" }} index={1} />
          ))}
          {SERVICES.slice(1, 3).map((s, i) => (
            <ServiceCard key={s.id} service={{ ...s, span: "md:col-span-6" }} index={i + 2} />
          ))}
          {SERVICES.slice(3).map((s, i) => (
            <ServiceCard key={s.id} service={{ ...s, span: "md:col-span-6" }} index={i + 4} />
          ))}
        </div>
      </div>
    </section>
  );
}
